import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Trash } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const Row = ({ a, on, toggle }) => (
  <tr data-testid={`cleanup-row-${a.user_id}`}>
    <td className="px-4 py-2"><Checkbox data-testid={`cleanup-check-${a.user_id}`} checked={on} onCheckedChange={toggle} /></td>
    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.email || a.user_id}{a.teammate && <span className="ml-2 text-[#A3A3A3]">teammate</span>}</td>
    <td className="px-3 py-2 text-xs text-[#A3A3A3]">{a.reason}</td>
    <td className="px-3 py-2 text-xs uppercase text-[#00FF9D]">{a.plan || "—"}</td>
    <td className="px-3 py-2 text-xs text-[#A3A3A3] whitespace-nowrap">{day(a.created_at)}</td>
  </tr>
);

export default function AdminCleanup() {
  const api = useApi();
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({ queryKey: ["admin-cleanup"], queryFn: () => api.get("/admin/cleanup/candidates"), retry: false });
  const [picked, setPicked] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (isLoading) return <p className="text-sm text-[#A3A3A3]">Checking accounts…</p>;
  if (error) return <p data-testid="admin-cleanup-denied" className="text-sm text-[#A3A3A3]">{error.response?.status === 403 ? "This page is only available to admins." : errText(error, "Couldn't load accounts")}</p>;
  const list = data.accounts;
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const all = list.length > 0 && picked.size === list.length;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup/delete", { user_ids: [...picked] });
      toast.success(`Deleted ${r.deleted} account${r.deleted === 1 ? "" : "s"}${r.failed ? `, ${r.failed} couldn't be deleted (try again)` : ""}${r.skipped ? `, ${r.skipped} skipped (no longer test accounts)` : ""}`);
      setPicked(new Set());
      await qc.invalidateQueries({ queryKey: ["admin-cleanup"] });
    } catch (e) { toast.error(errText(e, "Couldn't delete accounts")); }
    setBusy(false);
    setConfirm(false);
  };
  return (
    <div data-testid="admin-cleanup-page" className="max-w-5xl">
      <p className="font-mono text-xs text-[#A3A3A3] flex items-center gap-2"><ShieldCheck className="text-[#00FF9D]" /> ADMIN</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Test account cleanup</h1>
      <p className="mt-3 text-sm text-[#A3A3A3]">Accounts that look like leftovers from testing: test sign-in addresses, test email domains, or logins that no longer exist. Admin accounts never appear here. Tick the ones to delete; nothing is removed until you confirm.</p>
      <div className="mt-8 rounded-xl border border-[#2A2A2A] bg-[#121212] overflow-x-auto">
        {list.length === 0 ? <p data-testid="admin-cleanup-empty" className="p-6 text-sm text-[#A3A3A3]">No test accounts found. All clean.</p> : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#2A2A2A]">
              <tr><th className="px-4 py-3 text-left"><Checkbox data-testid="cleanup-check-all" checked={all} onCheckedChange={() => setPicked(all ? new Set() : new Set(list.map((a) => a.user_id)))} /></th>
                <th className="text-left px-3 py-3">Account</th><th className="text-left px-3 py-3">Why it's listed</th><th className="text-left px-3 py-3">Plan</th><th className="text-left px-3 py-3">Created</th></tr>
            </thead>
            <tbody className="divide-y divide-[#1E1E1E]">{list.map((a) => <Row key={a.user_id} a={a} on={picked.has(a.user_id)} toggle={() => toggle(a.user_id)} />)}</tbody>
          </table>
        )}
      </div>
      {list.length > 0 && (
        <button data-testid="cleanup-delete-btn" disabled={!picked.size || busy} onClick={() => setConfirm(true)}
          className="mt-6 flex items-center gap-2 rounded-full bg-[#FF5C7A] px-6 py-2.5 text-sm font-semibold text-[#050505] hover:bg-[#FF8FA3] transition-colors disabled:opacity-40">
          <Trash size={14} /> Delete {picked.size || ""} selected
        </button>
      )}
      <AlertDialog open={confirm} onOpenChange={(v) => !busy && setConfirm(v)}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog" className="bg-[#121212] border-[#2A2A2A] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {picked.size} account{picked.size === 1 ? "" : "s"} for good?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A3A3]">Their logins, data, connections and any subscriptions are removed. No emails are sent. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-cancel-btn" disabled={busy} className="rounded-full bg-transparent border-[#2A2A2A]">Keep them</AlertDialogCancel>
            <AlertDialogAction data-testid="cleanup-confirm-btn" disabled={busy} onClick={(e) => { e.preventDefault(); run(); }}
              className="rounded-full bg-[#FF5C7A] text-[#050505] hover:bg-[#FF8FA3]">{busy ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
